import { createContext } from 'react';

import type { VoiceDiagnostics, VoiceStatus } from './voice/voice-session.service';
import type {
  MioAgentAction,
  MioAgentMessage,
  MioAgentState,
  MioContext,
  MioLearnerProfile,
  MioLiveContext,
  MioMemory,
  MioTurnPhase,
} from '@mio/shared';

export type MioMode = 'chat' | 'call';
export interface MioState {
  open: boolean;
  mode: MioMode;
  tabId: string;
  messages: MioAgentMessage[];
  suggestions: string[];
  pending: boolean;
  /** What the running turn is doing while its text is still held back. */
  phase: MioTurnPhase | null;
  loading: boolean;
  connected: boolean;
  error: string | null;
  context: MioLiveContext | null;
  capabilities: MioAgentState['capabilities'];
  memory: MioMemory[];
  profile: MioLearnerProfile;
  voices: { id: string; name: string; category: string }[];
  voiceStatus: VoiceStatus;
  voiceCaption: string;
  voiceDiagnostics: VoiceDiagnostics | null;
  microphoneId: string;
  selectMicrophone: (deviceId: string) => Promise<void>;
  muted: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  send: (text: string, context?: MioContext) => Promise<void>;
  cancel: () => void;
  clear: () => Promise<void>;
  retry: () => void;
  startCall: () => Promise<void>;
  endCall: () => void;
  toggleMute: () => void;
  observeContext: (context: MioLiveContext) => void;
  actionHandler: (
    handler: ((action: MioAgentAction, signal: AbortSignal) => Promise<void>) | null,
  ) => void;
  saveMemory: (id: string, text: string) => Promise<void>;
  removeMemory: (id: string) => Promise<void>;
  resetMemory: () => Promise<void>;
  saveProfile: (profile: Partial<MioLearnerProfile>) => Promise<void>;
  loadVoices: () => Promise<void>;
}

export const MioStateContext = createContext<MioState | null>(null);
